import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";

const issues = [
  "Anxiety and social stress",
  "Depression and low mood",
  "School pressure and academic stress",
  "Identity and self-esteem",
  "Family conflict",
  "Peer relationships and social dynamics",
  "Behavioral concerns",
  "Life transitions and change",
];

const whatToExpect = [
  {
    num: "01",
    title: "A therapist who actually gets it",
    body: "Our teen therapists understand the unique pressures teens face today, from academic stress and social media to identity questions and family dynamics. We speak their language, not clinical jargon.",
    color: "#B4A6E3",
  },
  {
    num: "02",
    title: "A safe, judgment-free space",
    body: "Teens need to feel heard without judgment. Our sessions create that space where they can be honest about what's really going on, without fear of being dismissed or overanalyzed.",
    color: "#7D54FC",
  },
  {
    num: "03",
    title: "Practical tools for real situations",
    body: "We don't just talk about feelings. We build concrete skills teens can use. Coping strategies, communication techniques, and self-awareness tools that actually work in their daily life.",
    color: "#3C6FF8",
  },
  {
    num: "04",
    title: "Parent involvement, when it helps",
    body: "Depending on the situation, we may involve parents in sessions to improve family communication and understanding. We balance teen privacy with helpful family support.",
    color: "#6B742F",
  },
];

export default function TeenTherapy() {
  return (
    <>
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/teen/teen-hero.png"
              alt="Teen therapy at Perspective"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)" }} />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                Teen Therapy
              </p>
              <h1 className="font-fraunces text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.15] mb-6">
                Support for teens when{" "}
                <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                  everything feels overwhelming
                </em>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 md:p-4 max-w-lg">
                <p className="font-manrope text-[#2B2B2B]/90 text-lg leading-relaxed">
                  Ages 13–17. Telehealth sessions across New York, New Jersey, and Indiana. We help teens make sense of what's going on and build tools that actually help.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What we help with */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #B4A6E3, #7D54FC)" }} />
                    <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#7D54FC" }}>
                      What We Help With
                    </span>
                  </div>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-6">
                    Teens face challenges adults never had to navigate{" "}
                    <em className="not-italic" style={{ color: "#7D54FC" }}>we're here for that</em>
                  </h2>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-8">
                    The world teens are growing up in is more complex than ever. Academic pressure, social media, identity exploration, family stress, and the general uncertainty of adolescence can feel overwhelming. Our teen therapists specialize in creating a space where teens feel safe, heard, and equipped to handle what's in front of them.
                  </p>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                    {issues.map((issue) => (
                      <div key={issue} className="flex items-center gap-2.5">
                        <span className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-sm" style={{ color: "#7D54FC" }}></i>
                        </span>
                        <span className="font-manrope text-[14px] text-[#2B2B2B]/80">{issue}</span>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={320}>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="/therapists"
                      className="inline-flex items-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      style={{ background: "#7D54FC" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#6a40e8"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#7D54FC"}
                    >
                      Find a Teen Therapist <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-1.5 font-manrope text-[15px] font-medium text-[#2B2B2B]/78 hover:text-[#2B2B2B] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Have questions? <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </ScrollReveal>
              </div>
              <ScrollReveal variant="fadeRight" delay={0}>
                <div className="w-full h-[280px] sm:h-[340px] md:h-[500px] rounded-2xl overflow-hidden">
                  <img
                    src="/images/teen/teen-supporting.png"
                    alt="Teen therapy session"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* What to expect */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase block mb-4" style={{ color: "#6B742F" }}>
                  What to Expect
                </span>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Therapy designed for how teens actually think and feel
                </h2>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">
                  Our teen therapy approach is collaborative, respectful, and age-appropriate. We don't talk down to teens. We meet them where they are.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {whatToExpect.map((item, i) => (
                <ScrollReveal key={item.num} variant="fadeUp" delay={i * 90}>
                  <div className="rounded-2xl p-7 border bg-white/80" style={{ borderColor: item.color + "22" }}>
                    <div className="h-[3px] w-10 rounded-full mb-5" style={{ background: item.color }} />
                    <div className="flex items-start gap-4">
                      <span className="font-manrope text-sm font-bold shrink-0 mt-0.5" style={{ color: item.color }}>{item.num}</span>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-2">{item.title}</h3>
                        <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">{item.body}</p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing note */}
        <section className="w-full py-16 md:py-20">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Ready to find the right support for your teen?
                </h2>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-6 max-w-lg mx-auto">
                  Teen therapy sessions start at $160 per 45-50 minute session. We offer telehealth across NY, NJ, and Indiana, and in-person sessions in Larchmont. A free 15-minute consultation is available to find the right fit.
                </p>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/therapists"
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"}
                  >
                    Find a Therapist <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}